package com.daytimegaming.ourdailyfinances.presentation.account

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.presentation.preview.UpdateRealEstateHoldingPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.defaultColors
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import kotlin.random.Random

@Composable
fun UpdateRealEstateHoldingScreen(
    realEstateHoldingId: String,
    onNavigateBack: () -> Unit,
    onHoldingDeleted: () -> Unit,
    viewModel: UpdateRealEstateHoldingViewModel = koinViewModel(key = remember { Random.nextInt().toString() }) {
        parametersOf(realEstateHoldingId)
    }
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    LaunchedEffect(state.isSuccess) {
        if (state.isSuccess) {
            onNavigateBack()
        }
    }

    LaunchedEffect(state.isDeleted) {
        if (state.isDeleted) {
            onHoldingDeleted()
        }
    }

    UpdateRealEstateHoldingScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onNicknameChange = { viewModel.onNicknameChange(it) },
        onPurchasePriceChange = { viewModel.onPurchasePriceChange(it) },
        onMortgageAmountChange = { viewModel.onMortgageAmountChange(it) },
        onMortgageInterestRateChange = { viewModel.onMortgageInterestRateChange(it) },
        onUpdateHoldingClick = { viewModel.updateHolding() },
        onDeleteHoldingClick = { viewModel.deleteHolding() }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun UpdateRealEstateHoldingScreenContent(
    state: UpdateRealEstateHoldingState,
    onNavigateBack: () -> Unit,
    onNicknameChange: (String) -> Unit,
    onPurchasePriceChange: (String) -> Unit,
    onMortgageAmountChange: (String) -> Unit,
    onMortgageInterestRateChange: (String) -> Unit,
    onUpdateHoldingClick: () -> Unit,
    onDeleteHoldingClick: () -> Unit
) {
    var showDiscardConfirmationDialog by remember { mutableStateOf(false) }
    var showDeleteConfirmationDialog by remember { mutableStateOf(false) }

    var purchasePriceTextFieldValue by remember {
        mutableStateOf(TextFieldValue(text = state.purchasePrice, selection = TextRange(state.purchasePrice.length)))
    }
    LaunchedEffect(state.purchasePrice) {
        if (state.purchasePrice != purchasePriceTextFieldValue.text) {
            val diff = state.purchasePrice.length - purchasePriceTextFieldValue.text.length
            val newSelectionIndex = (purchasePriceTextFieldValue.selection.end + diff).coerceIn(0, state.purchasePrice.length)
            purchasePriceTextFieldValue = TextFieldValue(
                text = state.purchasePrice,
                selection = TextRange(newSelectionIndex)
            )
        }
    }

    var mortgageAmountTextFieldValue by remember {
        mutableStateOf(TextFieldValue(text = state.mortgageAmount, selection = TextRange(state.mortgageAmount.length)))
    }
    LaunchedEffect(state.mortgageAmount) {
        if (state.mortgageAmount != mortgageAmountTextFieldValue.text) {
            val diff = state.mortgageAmount.length - mortgageAmountTextFieldValue.text.length
            val newSelectionIndex = (mortgageAmountTextFieldValue.selection.end + diff).coerceIn(0, state.mortgageAmount.length)
            mortgageAmountTextFieldValue = TextFieldValue(
                text = state.mortgageAmount,
                selection = TextRange(newSelectionIndex)
            )
        }
    }

    var interestRateTextFieldValue by remember {
        mutableStateOf(TextFieldValue(text = state.mortgageInterestRate, selection = TextRange(state.mortgageInterestRate.length)))
    }
    LaunchedEffect(state.mortgageInterestRate) {
        if (state.mortgageInterestRate != interestRateTextFieldValue.text) {
            val diff = state.mortgageInterestRate.length - interestRateTextFieldValue.text.length
            val newSelectionIndex = (interestRateTextFieldValue.selection.end + diff).coerceIn(0, state.mortgageInterestRate.length)
            interestRateTextFieldValue = TextFieldValue(
                text = state.mortgageInterestRate,
                selection = TextRange(newSelectionIndex)
            )
        }
    }

    Scaffold(
        modifier = Modifier.fillMaxSize(),
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "Update Real Estate Holding",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                },
                navigationIcon = {
                    IconButton(
                        onClick = {
                            if (state.hasAnyInput) {
                                showDiscardConfirmationDialog = true
                            } else {
                                onNavigateBack()
                            }
                        }
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back",
                            tint = MaterialTheme.colorScheme.onSurface
                        )
                    }
                }
            )
        },
        bottomBar = {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom))
                    .padding(horizontal = 16.dp, vertical = 8.dp)
            ) {
                if (state.isLoading) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(56.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                    }
                } else {
                    Button(
                        onClick = onUpdateHoldingClick,
                        enabled = state.isSaveEnabled,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = MaterialTheme.colorScheme.primary,
                            contentColor = Color(0xFF003731),
                            disabledContainerColor = MaterialTheme.colorScheme.primary.copy(alpha = 0.5f),
                            disabledContentColor = Color(0xFF003731).copy(alpha = 0.5f)
                        ),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(56.dp)
                    ) {
                        Text(
                            text = "Update Info",
                            fontWeight = FontWeight.Bold,
                            style = MaterialTheme.typography.titleMedium
                        )
                    }
                }
            }
        }
    ) { innerPadding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (state.error != null) {
                Text(
                    text = state.error,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodyMedium,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
            }

            // Group 1: General Info (Editable)
            GlassCard(modifier = Modifier.fillMaxWidth()) {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = "General Info",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.primary
                    )
                    OutlinedTextField(
                        value = state.nickname,
                        onValueChange = onNicknameChange,
                        label = { Text("Nickname") },
                        singleLine = true,
                        enabled = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.defaultColors(),
                        keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words)
                    )
                }
            }

            // Group 2: Address Details (Simple Text)
            GlassCard(modifier = Modifier.fillMaxWidth()) {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = "Address Details",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.primary
                    )
                    Text(
                        text = buildString {
                            append(state.addressLine1)
                            if (state.addressLine2.isNotEmpty()) {
                                append(", ").append(state.addressLine2)
                            }
                            append("\n").append(state.city).append(", ").append(state.state).append(" ").append(state.zipCode)
                            if (state.county.isNotEmpty()) {
                                append("\n").append(state.county).append(" County")
                            }
                        },
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        lineHeight = 22.sp
                    )
                }
            }

            // Group 3: Financial Details (Editable)
            GlassCard(modifier = Modifier.fillMaxWidth()) {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = "Financial Details",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.primary
                    )
                    OutlinedTextField(
                        value = purchasePriceTextFieldValue,
                        onValueChange = { newValue ->
                            purchasePriceTextFieldValue = newValue
                            onPurchasePriceChange(newValue.text)
                        },
                        label = { Text("Purchase Price") },
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.defaultColors(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                    )
                    OutlinedTextField(
                        value = mortgageAmountTextFieldValue,
                        onValueChange = { newValue ->
                            mortgageAmountTextFieldValue = newValue
                            onMortgageAmountChange(newValue.text)
                        },
                        label = { Text("Mortgage Amount (Optional)") },
                        supportingText = {
                            Text(
                                text = "If your mortgage can be brought in through any of your financial institutions, do not include a value here as it will cause an improper calculation on the net worth screens.",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        },
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.defaultColors(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                    )
                    OutlinedTextField(
                        value = interestRateTextFieldValue,
                        onValueChange = { newValue ->
                            interestRateTextFieldValue = newValue
                            onMortgageInterestRateChange(newValue.text)
                        },
                        label = { Text("Mortgage Interest Rate (Optional %)") },
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.defaultColors(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal)
                    )
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            Button(
                onClick = { showDeleteConfirmationDialog = true },
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.errorContainer,
                    contentColor = MaterialTheme.colorScheme.onErrorContainer
                ),
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier
                    .fillMaxWidth()
                    .height(56.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Delete,
                    contentDescription = null,
                    modifier = Modifier.size(20.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = "Delete Real Estate Holding",
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.titleMedium
                )
            }
        }
    }

    if (showDiscardConfirmationDialog) {
        AlertDialog(
            onDismissRequest = { showDiscardConfirmationDialog = false },
            title = { Text("Discard Changes") },
            text = { Text("You have unsaved changes. Are you sure you want to discard them?") },
            confirmButton = {
                TextButton(
                    onClick = {
                        showDiscardConfirmationDialog = false
                        onNavigateBack()
                    }
                ) {
                    Text("Discard", color = MaterialTheme.colorScheme.error)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showDiscardConfirmationDialog = false }
                ) {
                    Text("Cancel")
                }
            }
        )
    }

    if (showDeleteConfirmationDialog) {
        AlertDialog(
            onDismissRequest = { showDeleteConfirmationDialog = false },
            title = { Text("Delete Real Estate Holding") },
            text = { Text("Are you sure you want to delete this real estate holding? This action cannot be undone.") },
            confirmButton = {
                TextButton(
                    onClick = {
                        showDeleteConfirmationDialog = false
                        onDeleteHoldingClick()
                    }
                ) {
                    Text("Delete", color = MaterialTheme.colorScheme.error)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showDeleteConfirmationDialog = false }
                ) {
                    Text("Cancel")
                }
            }
        )
    }
}

@PreviewLightDark
@Composable
fun UpdateRealEstateHoldingScreenPreview(
    @PreviewParameter(UpdateRealEstateHoldingPreviewParameterProvider::class) state: UpdateRealEstateHoldingState
) {
    AppTheme(darkTheme = isSystemInDarkTheme(), dynamicColor = false) {
        Surface {
            UpdateRealEstateHoldingScreenContent(
                state = state,
                onNavigateBack = {},
                onNicknameChange = {},
                onPurchasePriceChange = {},
                onMortgageAmountChange = {},
                onMortgageInterestRateChange = {},
                onUpdateHoldingClick = {},
                onDeleteHoldingClick = {}
            )
        }
    }
}
